// 收款 — PetPOS-style multi tender, one step at a time:
//   pick a method → the amount is pre-filled with what's still owed (type to
//   change it) → 加入. If it doesn't cover the bill, 尚欠 shows and you pick
//   the next method + amount. When nothing is owed, 確認收款 sends it all.
// A single tender is still one tap: the button confirms straight away when
// the current entry covers the bill. Cash takes the amount handed over and
// works out the change. Wallet / points can't buy a wallet top-up (server
// rule, mirrored here); wallet is whole dollars.
import { useEffect, useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { payLabel } from "./posReceipt.js";
import { Check, X, Printer, Banknote, Wallet, Star, CreditCard } from "lucide-react";
import { C, Btn, Sheet, money, round2, Label } from "./posUi.jsx";
import { posErrorText } from "./posMath.js";

const nextNotes = (amt) => {
  const out = [];
  for (const n of [10, 20, 50, 100, 200, 500, 1000]) {
    const v = Math.ceil(amt / n) * n;
    if (v > amt && !out.includes(v)) out.push(v);
  }
  return out.slice(0, 4);
};

export default function PaySheet({ offline = false, has = () => true, open, onClose, totals, customer, tenant, methods, sym, onSubmit, onDone, onPrint, onPromoStale }) {
  const [tenders, setTenders] = useState([]);
  const [sel, setSel] = useState("cash");
  const [amtStr, setAmtStr] = useState("");      // "" = use the suggested amount
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setTenders([]); setSel("cash"); setAmtStr(""); setErr(""); setResult(null); setBusy(false); } }, [open]);

  const total = totals?.total || 0;
  const paid = round2(tenders.reduce((s, t) => s + t.amount, 0));
  const remaining = round2(total - paid);
  const storedUsed = round2(tenders.filter(t => t.method === "wallet" || t.method === "points").reduce((s, t) => s + t.amount, 0));
  const storedCap = Math.max(0, round2(total - (totals?.topup || 0) - storedUsed));
  const walletBal = Math.floor(Number(customer?.wallet) || 0) - tenders.filter(t => t.method === "wallet").reduce((s, t) => s + t.amount, 0);
  const pointsBal = (Number(customer?.points) || 0) - tenders.filter(t => t.method === "points").reduce((s, t) => s + (t.points || 0), 0);
  const redeemValue = Math.max(0.01, Number(tenant?.loyaltyRedeemValue) || 1);
  const change = round2(tenders.reduce((s, t) => s + (t.tendered != null ? Math.max(0, t.tendered - t.amount) : 0), 0));

  const rows = useMemo(() => {
    const r = [{ key: "cash", method: "cash", name: zhEn("現金", "Cash"), icon: <Banknote size={20} /> }];
    if (customer && !offline && has("standard") && tenant?.walletEnabled !== false && Number(customer.wallet) > 0) r.push({ key: "wallet", method: "wallet", name: zhEn("錢包", "Wallet"), icon: <Wallet size={20} />, sub: zhEn("餘 ", "Bal. ") + money(Math.max(0, walletBal), sym) });
    if (customer && !offline && has("full") && tenant?.loyaltyEnabled && Number(customer.points) > 0) r.push({ key: "points", method: "points", name: zhEn("積分", "Points"), icon: <Star size={20} />, sub: Math.max(0, pointsBal) + zhEn(" 分", " pts") });
    for (const m of methods || []) r.push({ key: m.id, method: m.presetKey || m.name, name: m.name.replace(/^\p{Extended_Pictographic}\s*/u, ""), icon: <CreditCard size={20} /> });
    return r;
  }, [methods, customer, tenant, walletBal, pointsBal, sym, has, offline]);
  const row = rows.find(r => r.key === sel) || rows[0];

  // Most this method can pay right now.
  const maxFor = (r) => {
    if (r.method === "wallet") return Math.max(0, Math.min(Math.floor(walletBal), Math.floor(Math.min(remaining, storedCap))));
    if (r.method === "points") return Math.max(0, round2(Math.min(pointsBal, Math.floor(Math.min(remaining, storedCap) / redeemValue)) * redeemValue));
    return remaining;
  };
  const suggested = maxFor(row);
  const typed = amtStr === "" ? null : round2(Number(amtStr));

  // The entry the current step would add, or an error explaining why not.
  const entry = useMemo(() => {
    if (remaining <= 0) return null;
    const a = typed == null ? suggested : typed;
    if (!(a > 0)) return { error: row.method === "wallet" ? zhEn("錢包冇可用餘額", "No wallet balance") : row.method === "points" ? zhEn("冇可用積分", "No points available") : zhEn("請輸入金額", "Enter an amount") };
    if (row.method === "cash") {
      // Cash: the amount is what the customer hands over.
      return a >= remaining
        ? { method: "cash", methodName: "現金", amount: remaining, tendered: a }
        : { method: "cash", methodName: "現金", amount: a };
    }
    if (a > remaining + 0.001) return { error: zhEn("多過尚欠 ", "More than the amount still owed, ") + money(remaining, sym) };
    if (row.method === "wallet") {
      if (a !== Math.floor(a)) return { error: zhEn("錢包只可以用整數", "Wallet can only take whole dollars") };
      if (a > maxFor(row)) return { error: storedCap < remaining ? zhEn("錢包唔可以用嚟付儲值", "The wallet can't pay for a top-up") : zhEn("錢包餘額不足（最多 ", "Not enough wallet balance (max ") + money(maxFor(row), sym) + zhEn("）", ")") };
      return { method: "wallet", methodName: "錢包", amount: a };
    }
    if (row.method === "points") {
      const pts = Math.round(a / redeemValue);
      if (Math.abs(pts * redeemValue - a) > 0.001) return { error: zhEn("要係 ", "Must be a multiple of ") + money(redeemValue, sym) + zhEn(" 嘅倍數", "") };
      if (pts > pointsBal || a > storedCap + 0.001) return { error: zhEn("積分不足（最多抵 ", "Not enough points (max ") + money(maxFor(row), sym) + zhEn("）", ")") };
      return { method: "points", methodName: "積分", amount: a, points: pts };
    }
    return { method: row.method, methodName: row.name, amount: a };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, typed, suggested, row, storedCap, pointsBal, walletBal, redeemValue, sym]);
  const entryOk = entry && !entry.error;
  const afterEntry = entryOk ? round2(remaining - entry.amount) : remaining;

  const pick = (r) => { setSel(r.key); setAmtStr(""); setErr(""); };

  const submit = async (list) => {
    if (busy) return;
    setBusy(true); setErr("");
    try { setResult(await onSubmit(list)); }
    catch (e) { setErr(posErrorText(e)); if (String(e?.message || "").includes("promo_exhausted")) onPromoStale?.(); }
    finally { setBusy(false); }
  };
  const act = () => {
    if (remaining <= 0) return submit(tenders);
    if (!entryOk) return;
    const next = [...tenders, entry];
    if (afterEntry <= 0) return submit(next);
    // Partial: keep it, move on — cash is usually the last tender, so offer
    // another method next.
    setTenders(next); setAmtStr("");
    if (row.method !== "cash") setSel("cash");
    else { const other = rows.find(r => r.key !== "cash"); if (other) setSel(other.key); }
  };

  if (result) {
    return (
      <Sheet open={open} onClose={() => onDone(result)} title="">
        <div style={{ textAlign: "center", padding: "10px 0 6px" }}>
          <div style={{ width: 76, height: 76, borderRadius: 99, background: C.green, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 30px rgba(16,185,129,0.35)" }}>
            <Check size={42} color="#fff" strokeWidth={3.5} />
          </div>
          <p style={{ fontSize: 22, fontWeight: 900, color: C.ink, margin: 0 }}>{zhEn("收款完成", "Payment complete")}</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: C.sub, margin: "4px 0 18px" }}>{zhEn("單號 #", "Receipt #")}{result.number} · {money(result.total, sym)}</p>
          {result.change > 0 && (
            <div style={{ background: C.amberSoft, borderRadius: 20, padding: "16px 12px", marginBottom: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 900, color: "#B45309", margin: 0 }}>{zhEn("找續", "Change")}</p>
              <p style={{ fontSize: 40, fontWeight: 900, color: "#92400E", margin: 0, letterSpacing: -1 }}>{money(result.change, sym)}</p>
            </div>
          )}
          {result.pointsEarned > 0 && <p style={{ fontSize: 13, fontWeight: 800, color: C.green, margin: "0 0 10px" }}>{zhEn("⭐ 客戶賺咗", "⭐ Customer earned")} {result.pointsEarned} {zhEn("分", "pts")}</p>}
          {(result.appointments || []).some(a => a.paid) && <p style={{ fontSize: 13, fontWeight: 800, color: C.sub, margin: "0 0 10px" }}>{zhEn("✂️ 預約已標記為已付款", "✂️ Booking marked as paid")}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn kind="ghost" style={{ flex: 1 }} onClick={() => onPrint?.(result)}><Printer size={18} /> {zhEn("列印收據", "Print receipt")}</Btn>
            <Btn style={{ flex: 1.4 }} onClick={() => onDone(result)}>{zhEn("新一單", "New sale")}</Btn>
          </div>
        </div>
      </Sheet>
    );
  }

  const btnLabel = busy ? zhEn("處理中…", "Processing…")
    : remaining <= 0 ? zhEn("確認收款 ", "Confirm payment ") + money(total, sym) + (change > 0 ? zhEn(" · 找 ", " · change ") + money(change, sym) : "")
    : !entryOk ? zhEn("尚欠 ", "Still owed ") + money(remaining, sym)
    : afterEntry <= 0 ? zhEn("確認收款 · ", "Confirm payment · ") + payLabel(entry.methodName) + " " + money(entry.amount, sym) + (entry.tendered && entry.tendered > entry.amount ? zhEn(" · 找 ", " · change ") + money(round2(entry.tendered - entry.amount), sym) : "")
    : zhEn("加入 ", "Add ") + payLabel(entry.methodName) + " " + money(entry.amount, sym) + zhEn(" · 尚欠 ", " · still owed ") + money(afterEntry, sym);

  return (
    <Sheet open={open} onClose={busy ? undefined : onClose} title={zhEn("收款", "Payment")} maxWidth={620}
      footer={
        <div>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "0 0 8px", textAlign: "center" }}>{err}</p>}
          <Btn kind={remaining <= 0 || (entryOk && afterEntry <= 0) ? "green" : "primary"} disabled={busy || (remaining > 0 && !entryOk)} onClick={act} style={{ width: "100%", padding: 17, fontSize: 16 }}>
            {btnLabel}
          </Btn>
        </div>
      }>
      {/* what's owed + tenders so far */}
      <div style={{ textAlign: "center", margin: "0 0 12px" }}>
        <p style={{ fontSize: 13, fontWeight: 900, color: C.sub, margin: 0 }}>{tenders.length ? zhEn("尚欠", "Still owed") : zhEn("應收", "Amount due")}</p>
        <p style={{ fontSize: 40, fontWeight: 900, color: tenders.length && remaining > 0 ? C.accent : C.ink, margin: 0, letterSpacing: -1.5 }}>{money(Math.max(0, remaining), sym)}</p>
        {tenders.length > 0
          ? <p style={{ fontSize: 12, fontWeight: 800, color: C.sub, margin: "2px 0 0" }}>{zhEn("總數", "Total")} {money(total, sym)} {zhEn("· 已收", "· received")} {money(paid, sym)}</p>
          : (totals?.apt > 0 || totals?.topup > 0) && (
            <p style={{ fontSize: 12, fontWeight: 800, color: C.sub, margin: "2px 0 0" }}>
              {[totals.apt > 0 && zhEn("美容 ", "Grooming ") + money(totals.apt, sym), totals.retail > 0 && zhEn("零售 ", "Retail ") + money(totals.retail - totals.orderDiscount, sym), totals.topup > 0 && zhEn("儲值 ", "Top-up ") + money(totals.topup, sym)].filter(Boolean).join(" · ")}
            </p>
          )}
      </div>

      {tenders.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: "2px 12px", marginBottom: 14 }}>
          {tenders.map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: i ? "1px solid " + C.line : "none" }}>
              <span style={{ width: 22, height: 22, borderRadius: 99, background: C.greenSoft, color: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900 }}>{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink }}>{payLabel(t.methodName)}{t.points ? " · " + t.points + zhEn(" 分", " pts") : ""}</p>
                {t.tendered != null && <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("收", "Received")} {money(t.tendered, sym)} {zhEn("· 找", "· change")} {money(Math.max(0, t.tendered - t.amount), sym)}</p>}
              </div>
              <span style={{ fontSize: 16, fontWeight: 900, color: C.ink }}>{money(t.amount, sym)}</span>
              <button type="button" aria-label={zhEn("移除", "Remove")} onClick={() => { setTenders(ts => ts.filter((_, j) => j !== i)); setAmtStr(""); }} style={{ border: "none", background: "#F3F2F8", borderRadius: 99, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.sub }}><X size={14} strokeWidth={3} /></button>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 && (
        <>
          <Label>{tenders.length ? zhEn("第 ", "Payment method #") + (tenders.length + 1) + zhEn(" 個付款方式", "") : zhEn("付款方式", "Payment method")}</Label>
          {!(methods || []).length && <p style={{ margin: "-4px 2px 10px", fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("想收信用卡、轉數快？喺 收銀 → 設定 → 收款方式 加。", "Want card or FPS? Add them in POS → Settings → Payment methods.")}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(104px,1fr))", gap: 8, marginBottom: 14 }}>
            {rows.map(r => {
              const on = r.key === row.key;
              return (
                <button key={r.key} type="button" className="pos-tap" onClick={() => pick(r)} style={{
                  border: on ? "2px solid transparent" : "1.5px solid " + C.line, background: on ? C.grad : "#fff", color: on ? "#fff" : C.ink,
                  borderRadius: 16, padding: "10px 8px", minHeight: 66, cursor: "pointer", fontFamily: "inherit", textAlign: "center",
                  boxShadow: on ? "0 8px 18px rgba(255,92,138,0.32)" : "none", transform: on ? "scale(1.02)" : "none", transition: "all .12s",
                }}>
                  <span style={{ color: on ? "#fff" : C.accent, display: "inline-flex" }}>{r.icon}</span>
                  <p style={{ margin: "3px 0 0", fontSize: 14, fontWeight: 900 }}>{r.name}</p>
                  {r.sub && <p style={{ margin: 0, fontSize: 11, fontWeight: 800, opacity: 0.8 }}>{r.sub}</p>}
                </button>
              );
            })}
          </div>

          <div style={{ background: "#fff", border: "1.5px solid " + (entry?.error ? C.red : C.line), borderRadius: 18, padding: 14 }}>
            <p style={{ margin: "0 0 6px", fontSize: 12.5, fontWeight: 900, color: C.sub, textAlign: "center" }}>
              {row.method === "cash" ? zhEn("客人畀幾多（現金）", "Cash received from customer") : row.method === "wallet" ? zhEn("由錢包扣（整數）", "Take from wallet (whole dollars)") : row.method === "points" ? zhEn("用積分抵扣（每分 ", "Redeem points (each worth ") + money(redeemValue, sym) + zhEn("）", ")") : row.name + zhEn(" 收幾多", " amount to take")}
            </p>
            <input inputMode="decimal" value={amtStr} placeholder={String(suggested)} onChange={e => setAmtStr(e.target.value.replace(/[^\d.]/g, ""))}
              style={{ width: "100%", boxSizing: "border-box", border: "none", outline: "none", textAlign: "center", fontSize: 40, fontWeight: 900, color: C.ink, fontFamily: "inherit", background: "transparent", letterSpacing: -1 }} />
            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap", marginTop: 6 }}>
              <QuickBtn on={typed == null || typed === suggested} onClick={() => setAmtStr("")}>{row.method === "cash" ? zhEn("剛好 ", "Exact ") : row.method === "wallet" || row.method === "points" ? zhEn("用盡 ", "Use all ") : zhEn("全數 ", "Full ")}{money(suggested, sym)}</QuickBtn>
              {row.method === "cash" && nextNotes(remaining).map(n => <QuickBtn key={n} on={typed === n} onClick={() => setAmtStr(String(n))}>{money(n, sym)}</QuickBtn>)}
            </div>
            {entry?.error && <p style={{ margin: "8px 0 0", fontSize: 12.5, fontWeight: 800, color: C.red, textAlign: "center" }}>{entry.error}</p>}
            {entryOk && entry.points && <p style={{ margin: "8px 0 0", fontSize: 12.5, fontWeight: 800, color: C.sub, textAlign: "center" }}>= {entry.points} {zhEn("分（剩", "pts,")} {pointsBal - entry.points} {zhEn("分）", "pts left")}</p>}
            {entryOk && entry.tendered > entry.amount && <p style={{ margin: "8px 0 0", fontSize: 16, fontWeight: 900, color: "#B45309", textAlign: "center" }}>{zhEn("找續", "Change")} {money(round2(entry.tendered - entry.amount), sym)}</p>}
            {entryOk && afterEntry > 0 && <p style={{ margin: "8px 0 0", fontSize: 12.5, fontWeight: 800, color: C.accent, textAlign: "center" }}>{zhEn("部分付款 — 加入後再揀第二個方式付餘下", "Part payment — add it, then choose another method for the rest")} {money(afterEntry, sym)}</p>}
          </div>
        </>
      )}
    </Sheet>
  );
}

const QuickBtn = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick} style={{ border: "none", borderRadius: 12, padding: "9px 12px", fontFamily: "inherit", fontWeight: 900, fontSize: 14, cursor: "pointer", background: on ? C.greenSoft : "#F3F2F8", color: on ? "#047857" : C.ink }}>{children}</button>
);
